import React, { useState, useEffect } from 'react';
import axios from 'axios';
import BASE_URL from '../config/config';
import jsPDF from 'jspdf';
import 'jspdf-autotable'; // para generar tablas fácilmente

const typeMapping = {
  rrss: 'Redes Sociales',
  igualadiseo: 'Creativo',
  bug: 'Iguala SEM',
  task: 'Desarrollo',
  story: 'Sin especificar',
  epic: 'Marketing',
  igualamarketing: 'Iguala SEO',
  administracin: 'Administración'
};

const TareasPendientes = () => {
  // Determinar el año actual y usarlo como valor por defecto (si está entre 2020 y 2025)
  const currentYear = new Date().getFullYear();
  const defaultYear = currentYear >= 2020 && currentYear <= 2025 ? currentYear : 2020;

  const [tasks, setTasks] = useState([]); // Array de tareas
  const [selectedYear, setSelectedYear] = useState(defaultYear);
  const [filterTitle, setFilterTitle] = useState(''); // Filtro por título
  const [filterMonth, setFilterMonth] = useState(''); // Filtro por mes (valores: '' o 1-12)
  const [filterType, setFilterType] = useState(''); // Filtro por tipo (valores: '' o alguna clave de typeMapping)
  const [filterOverdue, setFilterOverdue] = useState(false); // Filtro para tareas atrasadas
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Cargar el fichero correspondiente cada vez que cambia el año seleccionado
  useEffect(() => {
    setLoading(true);
    setError(null);

    axios
      .get(`${BASE_URL}/cachedFiles/tareas_${selectedYear}.txt`)
      .then((response) => {
        let data;
        try {
          // Si el contenido es un string, se parsea a JSON
          data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
        } catch (err) {
          console.error('Error al parsear el JSON del archivo:', err);
          setError('Error al procesar los datos.');
          setLoading(false);
          return;
        }
        // Se espera que el JSON sea un array
        if (!Array.isArray(data)) {
          setError('La estructura de los datos no es la esperada.');
          setTasks([]);
        } else {
          setTasks(data);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error al obtener el archivo:', err);
        setError('Error al cargar las tareas.');
        setLoading(false);
      });
  }, [selectedYear]);

  // Función para determinar si la tarea está atrasada (más de 3 días después de la fecha de fin)
  const isOverdue = (fechaFin) => {
    const finishDate = new Date(fechaFin * 1000); // convertir a milisegundos
    const currentDate = new Date();
    const threeDaysInMs = 3 * 24 * 60 * 60 * 1000;
    return currentDate - finishDate > threeDaysInMs;
  };

// Filtrar tareas según el título, el mes (usando fecha de inicio), el tipo y si están atrasadas,
// y luego ordenarlas de más antiguas a más recientes según fechaInicio.
const filteredTasks = tasks
  .filter((task) => {
    const matchesTitle = task.titulo.toLowerCase().includes(filterTitle.toLowerCase());
    // Obtener el mes (1-12) de la fecha de inicio
    const taskMonth = new Date(task.fechaInicio * 1000).getMonth() + 1;
    const matchesMonth = filterMonth === '' || taskMonth === parseInt(filterMonth, 10);
    const matchesType = filterType === '' || task.tipo === filterType;
    const matchesOverdue = !filterOverdue || isOverdue(task.fechaFin);
    return matchesTitle && matchesMonth && matchesType && matchesOverdue;
  })
  .sort((a, b) => a.fechaInicio - b.fechaInicio); // Orden ascendente: de la más antigua a la más reciente


  // Manejar cambios en el filtro de año
  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
  };

  // Función para convertir una imagen desde una URL a Base64
  const getBase64FromUrl = async (url) => {
    const data = await fetch(url);
    const blob = await data.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  // Función auxiliar para obtener dimensiones de una imagen a partir de su Base64
  const getImageDimensions = (base64Data) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.onerror = reject;
      img.src = base64Data;
    });
  };

  // Función para exportar a PDF
  const handleExportPDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let currentY = 10; // posición vertical inicial

    // Construir la URL de la imagen usando process.env.PUBLIC_URL
    const logoUrl = `${process.env.PUBLIC_URL}/images/logo_gris_azul.png`;
    let logoBase64 = '';
    try {
      logoBase64 = await getBase64FromUrl(logoUrl);
    } catch (error) {
      console.error('Error al cargar el logo:', error);
    }

    // Si se obtuvo la imagen, se calcula el escalado y se centra
    if (logoBase64) {
      try {
        const { width: natWidth, height: natHeight } = await getImageDimensions(logoBase64);
        // Conversión de px a mm (1px ≈ 0.264583 mm)
        const natWidthMm = natWidth * 0.264583;
        const natHeightMm = natHeight * 0.264583;
        const maxWidthMm = 200 * 0.264583; // ≈53 mm
        let displayWidth = natWidthMm;
        let displayHeight = natHeightMm;
        if (displayWidth > maxWidthMm) {
          const scale = maxWidthMm / displayWidth;
          displayWidth = maxWidthMm;
          displayHeight = displayHeight * scale;
        }
        // Calcular la posición X para centrar la imagen
        const imgX = (pageWidth - displayWidth) / 2;
        const imgY = currentY;
        doc.addImage(logoBase64, 'PNG', imgX, imgY, displayWidth, displayHeight);
        currentY = imgY + displayHeight + 10; // dejar 10 mm de margen debajo de la imagen
      } catch (error) {
        console.error('Error al obtener dimensiones de la imagen:', error);
      }
    }

    // Agregar el título debajo de la imagen, en negrita y con tamaño 16
    const titleText = 'Informe de Tareas Pendientes';
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    const titleWidth = doc.getTextWidth(titleText);
    const titleX = (pageWidth - titleWidth) / 2;
    doc.text(titleText, titleX, currentY);
    currentY += 10; // margen debajo del título

    // Restablecer fuente normal para el resto del contenido
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);

    // Agregar la advertencia centrada
    const warningText = 'Advertencia: Si la tarea supera 3 días de atraso respecto a la fecha actual, se considera atrasada.';
    const warningWidth = doc.getTextWidth(warningText);
    const warningX = (pageWidth - warningWidth) / 2;
    doc.text(warningText, warningX, currentY);
    currentY += 10; // margen debajo de la advertencia

    // Preparar datos de la tabla de tareas filtradas (se agrega columna "Atrasada")
    const tableColumn = ["Título", "Fecha Inicio", "Fecha Fin", "Estado", "Dpto", "Atrasada"];
    const tableRows = [];
    filteredTasks.forEach(task => {
      const overdueIndicator = isOverdue(task.fechaFin) ? 'Sí' : '';
      const taskData = [
        task.titulo,
        new Date(task.fechaInicio * 1000).toLocaleDateString(),
        new Date(task.fechaFin * 1000).toLocaleDateString(),
        task.estado,
        typeMapping[task.tipo] || task.tipo,
        overdueIndicator
      ];
      tableRows.push(taskData);
    });

    if (tableRows.length === 0) {
      doc.text('No hay tareas que cumplan los criterios seleccionados.', 10, currentY);
    } else {
      // Definir márgenes para la tabla
      const marginLeft = 10;
      const marginRight = 10;
      const tableWidth = pageWidth - marginLeft - marginRight;
      // Calcular el ancho para cada grupo de columnas:
      // La columna "Título" ocupará el 30% y las 5 columnas restantes el 70% repartido de forma equitativa.
      const titleColWidth = tableWidth * 0.3;
      const otherColWidth = (tableWidth * 0.7) / 5;

      doc.autoTable({
        startY: currentY,
        margin: { left: marginLeft, right: marginRight },
        head: [tableColumn],
        body: tableRows,
        styles: { fontSize: 10 },
        headStyles: { fillColor: [38, 141, 194] }, // Color de la barra de encabezado: #268dc2
        columnStyles: {
          0: { cellWidth: titleColWidth },  // Columna "Título" ocupa el 30%
          1: { cellWidth: otherColWidth },
          2: { cellWidth: otherColWidth },
          3: { cellWidth: otherColWidth },
          4: { cellWidth: otherColWidth },
          5: { cellWidth: otherColWidth }
        }
      });
    }

    // Guardar el PDF
    doc.save(`Informe_Tareas_${selectedYear}.pdf`);
  };

  return (
    <main className="bg-gris">
      <div className="site-width grid-tareas-pendientes">
        <h2>Tareas Pendientes</h2>
        <p>Consulta las tareas pendientes.</p>
        <p className="warning">
          Si la tarea supera 3 días de atraso respecto a la fecha actual, se considera atrasada.
        </p>
        <div className="filtro-tareas-pendientes">
          <input
            type="text"
            placeholder="Buscar por título"
            value={filterTitle}
            onChange={(e) => setFilterTitle(e.target.value)}
          />
          <select id="typeFilter" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="">Todos los dpto</option>
            {Object.keys(typeMapping).map((key) => (
              <option key={key} value={key}>
                {typeMapping[key]}
              </option>
            ))}
          </select>
          <select id="monthFilter" value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)}>
            <option value="">Todos los meses</option>
            <option value="1">Enero</option>
            <option value="2">Febrero</option>
            <option value="3">Marzo</option>
            <option value="4">Abril</option>
            <option value="5">Mayo</option>
            <option value="6">Junio</option>
            <option value="7">Julio</option>
            <option value="8">Agosto</option>
            <option value="9">Septiembre</option>
            <option value="10">Octubre</option>
            <option value="11">Noviembre</option>
            <option value="12">Diciembre</option>
          </select>
          <select id="yearFilter" value={selectedYear} onChange={handleYearChange}>
            {[2020, 2021, 2022, 2023, 2024, 2025].map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          {/* Filtro para tareas atrasadas */}
          <label htmlFor="overdueFilter" className="label-tarea">
            <input
              type="checkbox"
              className="ui-checkbox"
              id="overdueFilter"
              checked={filterOverdue}
              onChange={(e) => setFilterOverdue(e.target.checked)}
            />
            Solo tareas atrasadas
          </label>

        </div>
        <button onClick={handleExportPDF} className='exportar-pdf'>Exportar PDF</button>


        {loading ? (
          <p>Cargando tareas...</p>
        ) : error ? (
          <p>{error}</p>
        ) : filteredTasks.length === 0 ? (
          <p>No hay tareas que cumplan los criterios seleccionados para el año {selectedYear}.</p>
        ) : (
          <div className="tareas-pendientes">
            {filteredTasks.map((task, index) => {
              const overdue = isOverdue(task.fechaFin);
              return (
                <div key={index} className="tarea-pendiente">
                  <div className="titulo-tarea-pendiente">
                    {overdue && <p className="tarea-atrasada">Tarea atrasada</p>}
                    <h3>{task.titulo}</h3>
                    <div className="cell-fecha">
                      <p className="cell-fecha-inicio">
                        Inicio: {new Date(task.fechaInicio * 1000).toLocaleDateString()}
                      </p>
                      <p className="cell-fecha-fin">
                        Fin: {new Date(task.fechaFin * 1000).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="estados-tarea">
                    <p className="tarea-estado">
                      <strong>Estado:</strong> {task.estado}
                    </p>
                    <p className="tarea-tipo">
                      <strong>Dpto:</strong> {typeMapping[task.tipo] || task.tipo}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </main>
  );
};

export default TareasPendientes;
